import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { injectIntl, intlShape } from '../../../util/reactIntl';
import { IconArrowHead, ResponsiveImage } from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ImageCarousel.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  showThumbnails: false,
  showFullscreenButton: false,
  slideDuration: 350,
};

const ImageCarousel = props => {
  const {
    intl,
    rootClassName,
    className,
    images,
    videos = [],
    imageVariants,
    startIndex = 0,
  } = props;
  const [currentIndex, setIndex] = useState(startIndex);
  const [isShowingVideo, setIsShowingVideo] = useState(false);

  useEffect(() => {
    setIndex(startIndex);
  }, [startIndex]);

  const imageItems = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ImageCarousel.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      image: img,
    };
  });

  const videoItems = videos.map((video, i) => {
    return {
      original: '',
      alt: intl.formatMessage(
        { id: 'ImageCarousel.videoAltText' },
        { index: i + 1, count: videos.length }
      ),
      video: video.url,
    };
  });

  // Images first, then videos - so clicking an image thumbnail shows that image
  const items = [...imageItems, ...videoItems];

  const renderItem = item => {
    if (item.video) {
      return (
        <div className={css.videoWrapper}>
          <video className={css.videoItem} controls loop muted playsInline autoPlay>
            <source src={item.video} type="video/mp4" />
          </video>
        </div>
      );
    }

    return (
      <div className={css.imageWrapper}>
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            sizes="(max-width: 767px) 100vw, 80vw"
          />
        </div>
      </div>
    );
  };

  const renderLeftNav = (onClick, disabled) => {
    return !isShowingVideo ? (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" className={css.arrowHead} />
        </div>
      </button>
    ) : null;
  };

  const renderRightNav = (onClick, disabled) => {
    return !isShowingVideo ? (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" className={css.arrowHead} />
        </div>
      </button>
    ) : null;
  };

  // If no image is given, render empty image.
  if (items.length === 0) {
    const classes = classNames(rootClassName || css.noImage, className);
    return <ResponsiveImage className={classes} image={null} variants={[]} alt="" />;
  }

  // We render index outside of ReactImageGallery.
  // This keeps track of current index aka slide changes happening inside gallery.
  const handleSlide = currentIndex => {
    setIndex(currentIndex);
    let item = items[currentIndex];
    setIsShowingVideo(item !== undefined && !!item?.video);
  };

  const naturalIndex = index => index + 1;

  // Render image index info. E.g. "4/12"
  const imageIndex =
    items.length > 0 ? (
      <span className={css.imageIndex}>
        {naturalIndex(currentIndex)}/{items.length}
      </span>
    ) : null;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <>
      <ReactImageGallery
        additionalClass={classes}
        items={items}
        renderItem={renderItem}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        onSlide={handleSlide}
        startIndex={currentIndex}
        {...IMAGE_GALLERY_OPTIONS}
      />
      {imageIndex}
    </>
  );
};

ImageCarousel.defaultProps = {
  rootClassName: null,
  className: null,
  videos: [],
};

const { string, arrayOf, shape } = PropTypes;

ImageCarousel.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  videos: arrayOf(
    shape({
      filename: string.isRequired,
      url: string.isRequired,
    })
  ),
  imageVariants: arrayOf(string).isRequired,
  intl: intlShape.isRequired,
  startIndex: PropTypes.number,
};

export default injectIntl(ImageCarousel);
